import React, { ReactElement } from 'react';
import { ColorFrom, Feature } from '@tencent/workbench';
import { AngleFrom, NumberInputFrom } from '@tencent/wb-ext-form';
import BaseDrawerFeature from '@framework/workbench/common/feature/base-drawer-feature';
import { ActionType } from '@src/framework/editor/src/js/constants/common-action-type';
import { canvasToPt, ptToCanvas } from '@src/slide/infrastructure/module/independent/unit';
import has from 'lodash/has';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import {
    BorderStyle,
    DirectList,
    getConfigByRect,
    getLinearDirection,
    LinearDirectList,
    getRectDirection,
    ShapeDirectionList,
} from '@src/framework/editor/src/js/constants';
import { alphaToOpacity } from '@framework/workbench/drawer/utils';
import {
    BorderType, IBorderInfo,
    ISketchStyle,
} from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IEndInfo, LineEndWidthType } from '@src/slide/infrastructure/module/dependent/line-info/endStyle';
import { BorderSketchType } from '@src/slide/domain/ui-model/element-modeles/property/border/constants';
import { IFillGradualValues } from '@framework/workbench/drawer/ui/text/fill/fill-interface';
import { LINEAR_GRADIENT, RADIAL_GRADIENT } from '@framework/workbench/drawer/ui/text/fill/config';
import _cloneDeep from 'lodash/cloneDeep';
import { FillFeature } from '@framework/workbench/drawer/ui/text/fill/fill-feature';
import { GradientType, IGradFill } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import { TdwDrawerConstMap, tdwDrawerReport } from '@framework/workbench/drawer/report';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import {
    DirectOptions,
    LinearDirectOptions,
    RECT_GRADIENT,
    RectDirectOptions,
    SHAPE_GRADIENT,
    ShapeDirectOptions,
} from '@framework/workbench/drawer/ui/fill/constants';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    TDWVer5Type,
    TDWVer6Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ExcludeSketchBorderProps } from '@src/slide/domain/ui-model/element-modeles/constant/props';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { CompoundType } from '@tencent/alloy-renderer-adapter';
import { handleInputBlurInitAndReset, IInputParams } from '@src/slide/application/service/drawer-input-manager/drawer-handler';
import { ColorPickerType, ColorSourceType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import {
    GradualSetting,
    IDirectionOption,
    OperationType,
    IBorderFeature,
    DrawerBorderType,
    WIDTH_MAX,
    IGradualPoint as IDrawerGradualPoint,
} from '@tencent/drawing-drawer-components';
import { ANGLE_MAX } from '@framework/workbench/drawer/common/config';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import { IGradualPoint } from '@framework/workbench/drawer/ui/fill/gradual-fill/gradual-fill-feature.interface';

export type IColorProps = {
    color: ITDColor;
    onlyChangeUI: boolean;
    from?: ColorFrom | number | NumberInputFrom | ColorSourceType;
    needReport?: boolean;
};

export type ArrowSizeProps = {
    lengthType: string,
    widthType: string,
};
const DEFAULT_COLOR = '#2f528f';
const DEFAULT_WIDTH = 0.75;
const DEFAULT_STYLE = 'solid';
// 渐变填充默认值
const DEFAULT_GRADUAL_ANGLE = 135;
const DEFAULT_GRADUAL_START_COLOR = '#FFF2CC';
const DEFAULT_GRADUAL_END_COLOR = '#FFB700';
const DEFAULT_GRADUAL_POINT_ARR = [
    {
        id: GradualSetting.startPoint,
        left: 0,
        bgColor: new TDColor({ rgbaStr: DEFAULT_GRADUAL_START_COLOR }),
        percent: 0,
    },
    {
        id: GradualSetting.endPoint,
        left: GradualSetting.gradientContentWidth - GradualSetting.pointWidth,
        bgColor: new TDColor({ rgbaStr: DEFAULT_GRADUAL_END_COLOR }),
        percent: 100,
    },
];

@Feature()
export class BorderFeature extends BaseDrawerFeature implements IBorderFeature {
    public curBorderType: string;
    public borderColor: ITDColor;
    public width: number;
    public opacity: number;
    public style;
    public headEnd;
    public tailEnd;
    public borderDom;
    // 渐变填充 - 对应的临时编辑数据
    public borderGradualValues: IFillGradualValues = {};
    // 供子类重写默认颜色
    protected defaultColor: string;
    protected defalutOpacity: number;
    protected previewStyle;
    protected previewColor: string;
    protected isPreivewStyle: boolean;    // 是否正在预览边框样式
    protected isPreviewColor: boolean;    // 是否正在预览边框颜色
    private sketchStyle?: ISketchStyle | null;
    private compoundType?: CompoundType | null;
    private isChangeGradualDirection = false;

    public constructor() {
        super();
        this.curBorderType = this.isMultiDifferent() ? DrawerBorderType.NO_SELECT : DrawerBorderType.NO_FILL;
        this.borderColor = new TDColor({
            rgbaStr: this.defaultColor || DEFAULT_COLOR,
        });
        this.width = DEFAULT_WIDTH;
        this.opacity = 0;
        this.style = DEFAULT_STYLE;
        this.headEnd = null;
        this.tailEnd = null;
        this.previewStyle = null;
        this.previewColor = '';
        this.addInitPropsCallback(this.getInputParams().key);
    }

    public initPropsCallback = () => {
        const hasNoBorder = this.borderType === DrawerBorderType.NO_FILL;
        const defaultWidth = this.getSelectModel()?.type === UIModelType.MULTIPLY ? Number.NaN : DEFAULT_WIDTH;
        this.width = hasNoBorder || typeof (this.borderInfo?.width) === 'undefined' ? defaultWidth : canvasToPt(this.borderInfo?.width);
        const borderColor = this.borderInfo?.tdColor;
        if (borderColor) {
            this.borderColor = borderColor;
            this.opacity = alphaToOpacity(this.borderColor.alpha) || 0;
        } else {
            this.borderColor = new TDColor({
                rgbaStr: this.defaultColor || DEFAULT_COLOR,
            });
            this.opacity = this.defalutOpacity ?? Number.NaN;
        }
        this.initGradualValues();
        this.updateUIWithBorderData();
    };

    public get borderInfo() {
        return (this.getSelectModel() as IMultiplyModel | IElementBaseModel).getBorder?.()
            .getBorderInfo();
    }

    public get borderType() {
        const borderType = (this.getSelectModel() as IMultiplyModel | IElementBaseModel).getBorder?.()
            .getBorderType();
        if (this.isMultiDifferent() || !borderType) {
            return DrawerBorderType.NO_SELECT;
        }
        const borderTypeMap = {
            [BorderType.GradualFill]: DrawerBorderType.GRAD_FILL,
            [BorderType.SolidFill]: DrawerBorderType.SOLID_FILL,
            [BorderType.NoFill]: DrawerBorderType.NO_FILL,
        };
        return borderTypeMap[borderType];
    }

    public handleBorderTypeChange = (value) => {
        if (this.curBorderType === value) {
            return;
        }
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        if (isNaN(this.width)) {
            this.width = DEFAULT_WIDTH;
        }
        this.curBorderType = value;
        this.updatePropsByType();
        this.tdwDrawerReport(FormItemName.BORDER_SELECT, { ver5: value });
    };

    public updateUIWithBorderData = () => {
        if (this.getAppService().editableServiceLayer?.previewService?.isPreview) {
            return;
        }
        const border = this.borderInfo;
        this.curBorderType = this.borderType;
        this.borderColor = border?.tdColor || new TDColor({
            rgbaStr: this.defaultColor || DEFAULT_COLOR,
        });
        this.style = border?.style || undefined;
        this.headEnd = border?.headEnd ? { ...border?.headEnd } : null;
        this.tailEnd = border?.tailEnd ? { ...border?.tailEnd } : null;
        this.sketchStyle = null;
        this.compoundType = border?.compoundType || undefined;
    };

    public shouldAngleDisabled(): boolean {
        return !(this.curBorderType === DrawerBorderType.GRAD_FILL
            && this.borderGradualValues.curGradualType === LINEAR_GRADIENT);
    }

    /**
     * 渐变填充 - 渲染编辑组件
     */
    public renderGradualFill = (): ReactElement => <GradualSetting
        pointArray={this.getPointArray()}
        activePointId={this.borderGradualValues.activePointId}
        updatePoint={this.handlePointChange}
        getDropContent={() => <ColorPickerContainer
            colorPickerType={ColorPickerType.StandardPC}
            onColorChange={this.handlePaletteGradualColorChange}
            selectedTdColor={this.borderGradualValues.activeColor}
        />}
    />;

    public getPointArray = () => this.borderGradualValues.pointArray;

    public handleColorChange = (props: IColorProps) => {
        // let { color } = props;
        const { color } = props;
        const { onlyChangeUI } = props;
        // if (!hasAlpha) {  // 使用上一个颜色的alpha值
        // const curAlpha = tdColor?.alpha || 1;
        // const rgb = getColorRGB(color);
        // color = tdColor?.srgb_color
        // }
        this.borderColor = color;
        // this.borderTdColor = tdColor;
        this.opacity = alphaToOpacity(color.alpha);
        this.updatePropsByType(onlyChangeUI);
        if (!onlyChangeUI) {
            // 切换颜色后将临时预览色值清空
            this.updatePreviewColor('');
        }
    };

    // hover 到颜色上时预览边框属性
    public handleColorEnter = (color) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        this.updatePreviewColor(color);
        this.updatePropsByType(true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
    };

    public handlePaletteLeave = () => {
        if (this.checkSelectType() > 1) {
            return;
        }
        this.updatePreviewColor('');
        this.updatePropsByType(true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    @handleInputBlurInitAndReset()
    public handleBorderWidthChange(value, from?: NumberInputFrom) {
        const { width } = this.getValidValue({ width: value }, from, WIDTH_MAX);
        this.getAppService().editableServiceLayer?.previewService
            ?.updateElementPreviewState(!!this.getDrawerInputManager()?.isFocus);
        this.handlePreviewState();
        this.width = width;
        this.updatePropsByType(this.isInputOnlyChangeUI(), true);
        this.tdwDrawerReport(FormItemName.BORDER_WIDTH, { ver5: width });
    }

    @handleInputBlurInitAndReset()
    public handleOpacityChange(value: number, dragEnd?: boolean, from?: NumberInputFrom) {
        const { opacity } = this.getValidValue({ opacity: value }, from);
        this.opacity = opacity;
        this.borderColor.updateOpacity(opacity);
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (onlyChangeUI) return;
        this.handleColorChange({ color: this.borderColor, onlyChangeUI, from, needReport: false });
        this.tdwDrawerReportNumberInput(FormItemName.BORDER_OPACITY, { ver6: opacity }, from, onlyChangeUI);
    }

    public handleBorderMouseLeave = () => {
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.borderDom.removeEventListener('mouseleave', this.handleBorderMouseLeave);
    };

    public handleNoBorderClick = () => {
        this.curBorderType = DrawerBorderType.NO_FILL;
        this.updatePreviewStyle(null);
        this.updatePropsByType();
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.tdwDrawerReport(FormItemName.BORDER_PRESET, { ver5: 1 });
    };

    public handleBorderStyleClick = (value, type: string) => {
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.style = value.style;
        if (typeof value.width !== 'undefined') {
            this.width = value.width;
        }
        if (type === FormItemName.BORDER_PRESET) {
            this.curBorderType = DrawerBorderType.SOLID_FILL;
        }
        this.updatePreviewStyle(null);
        this.updatePropsByType();
        this.handleBorderStyleReport(value, type);
    };

    public handleArrowStyleClick = (type: string, value: string | null) => {
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.updatePreviewStyle(null);
        let ver6;
        if (type === FormItemName.BORDER_ARROW_HEAD) {
            this.headEnd = this.headEnd
                ? { ...this.headEnd, lineEndType: value }
                : { lineEndType: value, lengthType: LineEndWidthType.MIDDLE, widthType: LineEndWidthType.MIDDLE };
            ver6 = this.tdwDrawerConstMap.arrowEnd.head;
        }
        if (type === FormItemName.BORDER_ARROW_TAIL) {
            this.tailEnd = this.tailEnd
                ? { ...this.tailEnd, lineEndType: value }
                : { lineEndType: value, lengthType: LineEndWidthType.MIDDLE, widthType: LineEndWidthType.MIDDLE };
            ver6 = this.tdwDrawerConstMap.arrowEnd.tail;
        }
        this.updatePropsByType();
        this.tdwDrawerReport(this.tdwDrawerConstMap.borderArrow, { ver5: value, ver6 });
    };

    public handleBorderMenuMouseEnter = (value, type?: string) => {
        if (this.checkSelectType() > 1) {
            return;
        }
        if (value && !value.width) {
            value.width = this.width;
        }
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(true);
        this.updatePreviewStyle(value);
        if (!value) {
            this.updateNoFillProps(true);
        } else if (type === FormItemName.BORDER_PRESET) {
            this.updateSolidProps(true);
        } else {
            this.updatePropsByType(true);
        }
    };

    public handleBorderMenuMouseLeave = () => {
        if (this.checkSelectType() > 1) {
            return;
        }
        if (!this.getAppService().editableServiceLayer?.previewService?.isPreview) {
            return;
        }
        this.updatePreviewStyle(null);
        this.updatePropsByType(true);
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
    };

    public handleArrowSizeClick = (type: string, value: ArrowSizeProps) => {
        this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(false);
        this.updatePreviewStyle(null);
        if (type === FormItemName.BORDER_ARROW_HEAD) {
            if (!this.headEnd) {
                this.headEnd = {};
            }
            this.headEnd.lengthType = value.lengthType;
            this.headEnd.widthType = value.widthType;
        }
        if (type === FormItemName.BORDER_ARROW_TAIL) {
            if (!this.tailEnd) {
                this.tailEnd = {};
            }
            this.tailEnd.lengthType = value.lengthType;
            this.tailEnd.widthType = value.widthType;
        }
        this.updatePropsByType();
    };

    public changeBorderColor = (tdColor: ITDColor, from: ColorFrom | ColorSourceType): void => {
        this.processTdColor(tdColor);
        this.handleColorChange({
            color: tdColor,
            onlyChangeUI: false,
            from,
        });
        this.getColorPickerManager()?.colorPickerTdwReport({
            color: tdColor.srgb_color,
            colorPickerFrom: TDWVer6Type.COLOR_PICKER_DRAWER,
            colorGroupFrom: from,
            actionType: TDWVer5Type.COLOR_PICKER_BORDER,
        });
    };

    /**
     * 渐变填充 - 切换类型
     * @param type
     */
    public handleGradualTypeChange = (type: string): void => {
        if (this.borderGradualValues.curGradualType !== type) {
            this.borderGradualValues.curGradualType = type;
            this.borderGradualValues.curDirect = this.getFillDirectionOptions()[0];
        }
        this.updatePropsByType();
        // 上报数据
        const ver5 = type === LINEAR_GRADIENT ? 1 : 2;
        this.tdwDrawerReport(TdwDrawerConstMap.borderGradualFillLinerOrRadial, { ver5 });
    };

    /**
     * 渐变填充 - 切换线性角度
     * @param value
     * @param from
     * @param isDragging
     */
    @handleInputBlurInitAndReset()
    public handleGradualAngleChange(value: number, from?: AngleFrom, dragEnd?: boolean): void {
        const { curAngle } = this.getValidValue({ curAngle: value }, from, ANGLE_MAX);
        const angle = curAngle % 360;
        this.borderGradualValues.curAngle = angle;
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (onlyChangeUI) return;
        this.updatePropsByType(onlyChangeUI);
        // 上报数据
        this.tdwDrawerReportAngle(FormItemName.BORDER_GRADUAL_ANGLE, from, angle);
    }

    /**
     * 渐变填充 - 切换径向方向
     * @param value
     */
    public handleGradualDirectionChange = (value: IDirectionOption): void => {
        this.borderGradualValues.curDirect = value;
        this.isChangeGradualDirection = true;
        this.updatePropsByType();
        // 上报数据
        this.tdwDrawerReport(FormItemName.BORDER_GRADUAL_DIRECTION, { ver5: value?.title });
    };

    /**
     * 渐变填充 - 透明度变更
     * @param opacity
     * @param from
     * @param onlyChangeUI
     */
    @handleInputBlurInitAndReset()
    public handleGradualOpacityChange(opacity: number, from?: NumberInputFrom, dragEnd?: boolean): void {
        const { gradualOpacity } = this.getValidValue({ gradualOpacity: opacity }, from);
        const newColor = this.borderGradualValues.activeColor || DEFAULT_GRADUAL_POINT_ARR[0].bgColor;
        newColor?.updateOpacity(gradualOpacity);
        this.borderGradualValues.activeColor = newColor;
        const onlyChangeUI = this.isInputOnlyChangeUIWithDrag(dragEnd);
        if (onlyChangeUI) return;
        const curPoint = this.borderGradualValues.pointArray.find(e => e.id === this.borderGradualValues.activePointId);
        if (curPoint) {
            curPoint.bgColor = newColor;
            this.updatePropsByType(onlyChangeUI);
            // 上报数据
            this.tdwDrawerReportNumberInput(FormItemName.BORDER_GRADUAL_OPACITY, { ver6: gradualOpacity }, from);
        }
    }

    public getFillDirectionOptions() {
        if (this.borderGradualValues.curGradualType === LINEAR_GRADIENT) {
            return LinearDirectOptions;
        }
        if (this.borderGradualValues.curGradualType === RECT_GRADIENT) {
            return RectDirectOptions;
        }
        if (this.borderGradualValues.curGradualType === SHAPE_GRADIENT) {
            return ShapeDirectOptions;
        }
        return DirectOptions;
    }

    public handleSolidFillColorChange = (color: string, from) => {
        this.getColorPickerManager()?.setRecentColor(color);
        this.changeBorderColor(new TDColor({ rgbaStr: color }), from);
    };

    /**
     * 获取输入框装饰符所需的参数
     */
    public getInputParams(): IInputParams {
        return {
            key: INPUT_PROPS_CALLBACK_KEY.BORDER,
        };
    }

    public handleBorderCompoundTypeClick = (newType) => {
        if (newType === this.borderInfo?.compoundType
            || (!this.borderInfo?.compoundType && newType === CompoundType.SINGLE)) {
            return;
        }
        this.compoundType = newType;
        this.updatePropsByType();
    };

    public handleBorderSketchStyleClick = (value) => {
        const { type } = value;
        if (type === this.borderInfo?.sketchStyle?.type
            || (!this.borderInfo?.sketchStyle?.type && type === BorderSketchType.NONE)) {
            return;
        }
        this.sketchStyle = {
            type,
        };
        this.updatePropsByType();
    };

    /**
     * 渐变填充 - 渐变点切换
     * @param id
     * @param pointArray
     * @param onlyChangeUI
     * @param operation
     */
    public handlePointChange = (
        id: string,
        pointArray: IDrawerGradualPoint[],
        onlyChangeUI: boolean,
        operation?: string,
    ): void => {
        pointArray = pointArray.map((item) => {
            const bgColor = item.bgColor instanceof TDColor ? item.bgColor
                : new TDColor({ rgbaStr: item.bgColor.srgbColor });
            return {
                ...item,
                bgColor,
            };
        }) as IGradualPoint[];
        // 切换渐变点时，强刷一下，否则透明度不更新
        if (this.borderGradualValues.activePointId !== id) {
            this.formService.$forceUpdate();
        }
        this.borderGradualValues.activePointId = id || this.borderGradualValues.activePointId;
        this.borderGradualValues.activeColor = (pointArray as IGradualPoint[])
            .find(e => e.id === this.borderGradualValues.activePointId)?.bgColor
            || this.borderGradualValues.activeColor;
        this.borderGradualValues.pointArray = pointArray || this.borderGradualValues.pointArray;
        if (onlyChangeUI) {
            return;
        }
        this.updatePropsByType();
        if (operation) {
            const formItemKey = {
                [OperationType.ADD]: TdwDrawerConstMap.borderGradualAddPoint,
                [OperationType.REMOVE]: TdwDrawerConstMap.borderGradualRemovePoint,
                [OperationType.REVERT]: TdwDrawerConstMap.borderGradualRevertPoint,
            }[operation];
            tdwDrawerReport(formItemKey);
        }
    };

    /**
     * 渐变填充 - 色板切换
     * @param color
     * @param from
     */
    public handlePaletteGradualColorChange = (color: ITDColor, from: ColorSourceType): void => {
        const curPoint = this.borderGradualValues.pointArray.find(e => e.id === this.borderGradualValues.activePointId);
        if (curPoint) {
            this.borderGradualValues.activeColor = color;
            curPoint.bgColor = color;
            this.updatePropsByType();
            // 上报数据
            let ver5 = 1;
            if (from === ColorSourceType.Custom) {
                ver5 = 3;
            } else if (from === ColorSourceType.Recent) {
                ver5 = 2;
            }
            this.tdwDrawerReport(TdwDrawerConstMap.borderGradualPointColorChange, { ver5 });
        }
    };

    protected updateBorder(border, onlyChangeUI, isPreviewColor?) {
        (this.getSelectModel() as IMultiplyModel | IElementBaseModel).getBorder?.()
            .updateCurrentBorder(border, onlyChangeUI, isPreviewColor);
    }

    protected updateNoFillProps(onlyChangeUI?: boolean) {
        const border = { color: null, gradFill: null, width: 0 };
        this.updateBorder(border, onlyChangeUI);
    }

    protected isMultiDifferent(): boolean {
        return this.isMultiDiffValue(ActionType.BORDERCOLOR, ExcludeSketchBorderProps);
    }

    /**
     * 处理tdColor, 图表fill-feature需要处理color
     */
    protected processTdColor(color: TDColor) {
        return color;
    }

    /**
     * 根据边框类型进行不同的更新
     * @param onlyChangeUI 是否只更新ui
     * @param isWidthChange 是否是边框宽度变更（边框边框比较特殊，宽度没有预览，但是要有在非预览编辑时隐藏选中框的能力，所以做了特殊处理）
     * @private
     */
    protected updatePropsByType(onlyChangeUI?: boolean, isWidthChange = false) {
        if (this.curBorderType === DrawerBorderType.NO_FILL) {
            this.updateNoFillProps(onlyChangeUI);
        } else if (this.curBorderType === DrawerBorderType.SOLID_FILL) {
            this.updateSolidProps(onlyChangeUI, isWidthChange);
        } else if (this.curBorderType === DrawerBorderType.GRAD_FILL) {
            this.updateGradualProps(onlyChangeUI, isWidthChange);
        }
    }

    private updateSolidProps(onlyChangeUI?: boolean, isWidthChange = false) {
        const fillStyle: { gradFill?: any; color?: any, tdColor?: ITDColor } = {};
        fillStyle.color = this.previewColor || this.borderColor.srgb_color;
        fillStyle.tdColor = this.borderColor;
        if (this.borderInfo?.gradFill) {
            // 预览时，从渐变变成纯色需要把gradFill置为null，否则updateUiProps不会将gradFill置为空
            fillStyle.gradFill = null;
        }
        const { headEnd, tailEnd } = this.getHeadAndTailEndProps(onlyChangeUI);
        let border: IBorderInfo = {};
        border = {
            width: this.getWidthProps(onlyChangeUI),
            style: (onlyChangeUI && has(this.previewStyle, 'style') ? this.previewStyle.style : this.style) || DEFAULT_STYLE,
            headEnd,
            tailEnd,
            ...fillStyle,
            sketchStyle: this.sketchStyle,
            compoundType: this.compoundType,
        };
        // 边框的隐藏选中框不在此做处理，单独有自己的更新监听逻辑
        if (this.checkSelectType() <= 1 && !isWidthChange) {
            // 隐藏选中框
            this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(!!onlyChangeUI);
        }
        this.updateBorder(border, onlyChangeUI, !!(onlyChangeUI && this.previewColor));
    }

    private updateGradualProps(onlyChangeUI?: boolean, isWidthChange = false) {
        const fillStyle: { gradFill?: IGradFill; color?: string | null } = {};
        if (Object.keys(this.borderGradualValues).length === 0) {
            this.borderGradualValues = {
                activePointId: DEFAULT_GRADUAL_POINT_ARR[0].id,
                activeColor: DEFAULT_GRADUAL_POINT_ARR[0].bgColor,
                curGradualType: LINEAR_GRADIENT,
                curDirect: LinearDirectList[2],
                curAngle: DEFAULT_GRADUAL_ANGLE,
                pointArray: _cloneDeep(DEFAULT_GRADUAL_POINT_ARR),
            };
        }
        // 渐变填充字段
        fillStyle.gradFill = this.getGradualValues();
        fillStyle.color = null;
        const { headEnd, tailEnd } = this.getHeadAndTailEndProps(onlyChangeUI);
        let border = {};
        border = {
            width: this.getWidthProps(onlyChangeUI),
            style: (onlyChangeUI && has(this.previewStyle, 'style') ? this.previewStyle.style : this.style) || DEFAULT_STYLE,
            headEnd,
            tailEnd,
            ...fillStyle,
            sketchStyle: this.sketchStyle,
            compoundType: this.compoundType,
        };
        if (this.checkSelectType() <= 1 && !isWidthChange) {
            // 隐藏选中框
            this.getAppService().editableServiceLayer?.previewService?.updateElementPreviewState(!!onlyChangeUI);
        }
        this.updateBorder(border, onlyChangeUI, !!(onlyChangeUI && this.previewColor));
        this.isChangeGradualDirection = false;
    }

    /**
     * 修改预览边框样式值
     */
    private updatePreviewStyle = (previewStyle) => {
        this.previewStyle = previewStyle;
    };

    /**
     * 修改预览边框颜色值
     */
    private updatePreviewColor = (previewColor) => {
        this.previewColor = previewColor;
    };

    private handleBorderStyleReport = (value, type: string) => {
        if (type === FormItemName.BORDER_LINE) {
            let ver5;
            BorderStyle.forEach((item, index) => {
                if (item.style === value.style) {
                    ver5 = index + 1;
                }
            });
            this.tdwDrawerReport(FormItemName.BORDER_LINE, { ver5 });
        } else {
            this.tdwDrawerReport(FormItemName.BORDER_PRESET, { ver5: value.style });
        }
    };

    // 输入框聚焦且变化时去掉选择框，移出边框设置面板或者输入框失焦时时选择框重现
    private handlePreviewState = () => {
        this.borderDom = document.getElementsByClassName('dui-tabpane-active')?.[0]?.children[1];
        this.borderDom?.addEventListener('mouseleave', this.handleBorderMouseLeave);
    };

    private getWidthProps = (onlyChangeUI?: boolean): number => {
        let w = isNaN(this.width) ? 0 : this.width;
        if (onlyChangeUI && has(this.previewStyle, 'width')) {
            w = isNaN(parseFloat(this.previewStyle.width)) ? 0 : this.previewStyle.width;
        }
        return ptToCanvas(parseFloat(String(w)));
    };

    private getHeadAndTailEndProps = (onlyChangeUI?: boolean): {
        headEnd?: IEndInfo | null;
        tailEnd?: IEndInfo | null;
    } => {
        let headEnd = null;
        let tailEnd = null;
        if (onlyChangeUI && this.previewStyle?.headEnd) {
            headEnd = this.previewStyle.headEnd;
        } else if (this.headEnd) {
            headEnd = this.headEnd;
        }
        if (onlyChangeUI && this.previewStyle?.tailEnd) {
            tailEnd = this.previewStyle.tailEnd;
        } else if (this.tailEnd) {
            tailEnd = this.tailEnd;
        }
        return { headEnd, tailEnd };
    };

    /**
     * 初始化编辑渐变填充时的临时数据
     */
    private initGradualValues = (): void => {
        const gradualData = this.borderInfo?.[BorderType.GradualFill];
        if (!gradualData) {
            this.borderGradualValues = {
                activePointId: DEFAULT_GRADUAL_POINT_ARR[0].id,
                activeColor: DEFAULT_GRADUAL_POINT_ARR[0].bgColor,
                curGradualType: LINEAR_GRADIENT,
                curDirect: LinearDirectList[2],
                curAngle: DEFAULT_GRADUAL_ANGLE,
                pointArray: _cloneDeep(DEFAULT_GRADUAL_POINT_ARR),
            };
            return;
        }
        const { curAngle, curGradualType, curDirect } = this.processGradualData(gradualData);
        // 渐变点
        let pointArray = _cloneDeep(DEFAULT_GRADUAL_POINT_ARR);
        let { activePointId, activeColor } = this.borderGradualValues;
        const gsList = gradualData?.gsList;
        if (gsList?.length) {
            // 新的pointArray
            pointArray = gsList.map((item, index) => FillFeature.transformPoint(index, gradualData, item));
            // 找到当前选中的渐进点index
            let curIndex = (this.borderGradualValues.pointArray || []).findIndex(e => e.id === activePointId);
            curIndex = curIndex === - 1 ? 0 : curIndex;
            // 转成成新的id
            activePointId = pointArray[curIndex].id;
            activeColor = pointArray.find(e => e.id === activePointId)?.bgColor || this.borderGradualValues.activeColor;
        }
        this.borderGradualValues = {
            ...this.borderGradualValues,
            pointArray,
            activePointId,
            activeColor,
            curAngle,
            curGradualType,
            curDirect,
        };
    };

    private processGradualData(gradualData: IGradFill) {
        // 渐变类型、角度（线性）、方向（径向）
        let { curAngle, curGradualType, curDirect } = this.borderGradualValues;
        if (gradualData?.pathGradient) {
            const pathGrad = gradualData.pathGradient;
            if (pathGrad.path === GradientType.Linear) {
                curGradualType = LINEAR_GRADIENT;
                if (gradualData?.lin) {
                    curAngle = gradualData.lin.ang;
                }
                curDirect = getLinearDirection(curAngle ?? 0) as any;
            } else if (pathGrad.path === GradientType.Rect) {
                curGradualType = RECT_GRADIENT;
                curDirect = getRectDirection(pathGrad.fillToRect) as any;
            } else if (pathGrad.path === GradientType.Shape) {
                curGradualType = SHAPE_GRADIENT;
                curDirect = ShapeDirectionList[0] as any;
            } else {
                curGradualType = RADIAL_GRADIENT;
                curDirect = getConfigByRect(pathGrad.fillToRect);
                curAngle = undefined;
            }
        }
        return { curAngle, curGradualType, curDirect };
    }

    /**
     * 从临时编辑数据整合最新的渐变填充数据
     */
    private getGradualValues = (): IGradFill => {
        const values: any = {};
        values.gsList = this.borderGradualValues.pointArray.map((item) => {
            const color = item.bgColor;
            return  { pos: item.percent, color, id: '' };
        });
        if (this.borderGradualValues.curGradualType === LINEAR_GRADIENT) {
            // 线性渐变
            const angle = this.isChangeGradualDirection
                ? this.borderGradualValues.curDirect?.angle : this.borderGradualValues.curAngle;
            values.lin = { ang: angle ?? DEFAULT_GRADUAL_ANGLE, scaled: 1 };
        } else {
            // 其他渐变
            let path = GradientType.Radial;
            if (this.borderGradualValues.curGradualType === RECT_GRADIENT) {
                path = GradientType.Rect;
            } else if (this.borderGradualValues.curGradualType === SHAPE_GRADIENT) {
                path = GradientType.Shape;
            }
            const curDirect = this.borderGradualValues.curDirect || DirectList[0];
            const fillToRect = Object.assign({}, curDirect.rect);
            values.pathGradient = { path, fillToRect };
            values.tileRect = Object.assign({}, curDirect.tileRect);
        }
        return values;
    };

    private getBorderSketchManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
        return appServices.editableServiceLayer?.borderSketchManager;
    }
}
